import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { getInterviewResults } from '../services/supabase';
import { 
  PlayIcon, 
  DownloadIcon, 
  ArrowLeftIcon, 
  UserIcon, 
  StarIcon, 
  ClockIcon,
  SearchIcon,
  SlidersIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

export function InterviewResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [results, setResults] = useState<any[]>([]);
  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scoreFilter, setScoreFilter] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!id) {
      setError('Interview ID is required');
      return;
    }
    loadResults();
  }, [id]);

  useEffect(() => {
    filterAndSortResults();
  }, [results, searchTerm, scoreFilter, sortOrder]);

  const loadResults = async () => {
    try {
      const data = await getInterviewResults(id!);
      const sortedData = data.sort((a, b) => (b.score || 0) - (a.score || 0));
      setResults(sortedData || []);
      setFilteredResults(sortedData || []);
      if (sortedData && sortedData.length > 0) {
        setSelectedCandidate(sortedData[0]);
      }
    } catch (error) {
      console.error('Error loading results:', error);
      setError(error instanceof Error ? error.message : 'Failed to load results');
      toast.error(t('interview.results.error'));
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortResults = () => {
    let filtered = [...results];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(candidate => 
        candidate.candidate_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.candidate_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply score filter
    if (scoreFilter !== null) {
      filtered = filtered.filter(candidate => 
        (candidate.score || 0) >= scoreFilter
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const scoreA = a.score || 0;
      const scoreB = b.score || 0;
      return sortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
    });

    setFilteredResults(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('interview.results.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('interview.results.title')}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('interview.results.backToDashboard')}
          </button>
        </div>

        {results.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600">
              {t('interview.results.noResponses')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Candidate List */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="mb-4 space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('interview.results.searchCandidates')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>

                  {/* Filters */}
                  <div className="flex items-center gap-2">
                    <select
                      value={scoreFilter || ''}
                      onChange={(e) => setScoreFilter(e.target.value ? Number(e.target.value) : null)}
                      className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">{t('interview.results.allScores')}</option>
                      <option value="80">{t('interview.results.above80')}</option>
                      <option value="60">{t('interview.results.above60')}</option>
                      <option value="40">{t('interview.results.above40')}</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {sortOrder === 'desc' ? (
                        <ArrowDownIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ArrowUpIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filteredResults.map((candidate) => (
                    <button
                      key={candidate.id}
                      onClick={() => setSelectedCandidate(candidate)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedCandidate?.id === candidate.id
                          ? 'bg-indigo-50 border-2 border-indigo-200'
                          : 'hover:bg-gray-50'
                      } ${(candidate.score || 0) >= 60 ? 'bg-green-50 hover:bg-green-100' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{candidate.candidate_name}</div>
                          <div className="text-sm text-gray-500 truncate">{candidate.candidate_email}</div>
                        </div>
                        {candidate.score !== null && (
                          <div className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(candidate.score)}`}>
                            {candidate.score}%
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Candidate Details */}
            {selectedCandidate && (
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedCandidate.candidate_name}
                      </h2>
                      <p className="text-gray-500">{selectedCandidate.candidate_email}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <ClockIcon className="h-4 w-4" />
                        {formatDate(selectedCandidate.created_at)}
                      </div>
                    </div>
                    {selectedCandidate.score !== null && (
                      <div className="text-center">
                        <div className={`text-3xl font-bold px-4 py-2 rounded-lg ${getScoreColor(selectedCandidate.score)}`}>
                          {selectedCandidate.score}%
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {t('interview.results.overallScore')}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {selectedCandidate.responses?.map((response: any, index: number) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="font-medium text-gray-900 mb-2">
                          Q{index + 1}: {response.question}
                        </div>
                        <div className="text-gray-600 mb-4 whitespace-pre-wrap">
                          {response.answer}
                        </div>
                        
                        {response.videoUrl && (
                          <div className="flex items-center gap-4 mb-4">
                            <a
                              href={response.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100"
                            >
                              <PlayIcon className="h-4 w-4" />
                              {t('interview.results.watchVideo')}
                            </a>
                            <a
                              href={response.videoUrl}
                              download
                              className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100"
                            >
                              <DownloadIcon className="h-4 w-4" />
                              {t('interview.results.downloadVideo')}
                            </a>
                          </div>
                        )}

                        {response.feedback && (
                          <div className="border-t border-gray-200 pt-4 mt-4">
                            <div className="text-sm text-gray-600 mb-2">
                              {response.feedback.feedback}
                            </div>
                            <div className="flex items-center gap-4">
                              <div className={`text-sm px-2 py-1 rounded-full font-medium ${getScoreColor(response.feedback.score)}`}>
                                {t('interview.results.score')}: {response.feedback.score}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}